'use client'

import React from 'react'
import { useTheme } from '../theme/provider'
import { Loader2 } from 'lucide-react'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Button visual variant
   */
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost'
  /**
   * Button size
   */
  size?: 'sm' | 'md' | 'lg'
  /**
   * Loading state - shows spinner and disables button
   */
  loading?: boolean
  /**
   * Full width button
   */
  fullWidth?: boolean
  /**
   * Children content
   */
  children: React.ReactNode
  /**
   * Optional icon to display before text
   */
  icon?: React.ReactNode
  /**
   * Custom class name
   */
  className?: string
}

/**
 * Button Component - Real Madrid Style
 *
 * Reusable button component with multiple variants and sizes.
 * Based on Real Madrid website button patterns.
 *
 * Features:
 * - Multiple variants: primary, secondary, outline, ghost
 * - Three sizes: sm, md, lg
 * - Loading state with spinner
 * - Theme-aware colors
 * - Hover/active animations
 * - Icon support
 *
 * @example
 * ```tsx
 * <Button variant="primary" size="lg" onClick={handleClick}>
 *   Click Me
 * </Button>
 *
 * <Button variant="outline" loading={isLoading}>
 *   Load More
 * </Button>
 *
 * <Button variant="ghost" size="sm" icon={<ArrowRight />}>
 *   Next
 * </Button>
 * ```
 */
export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  children,
  icon,
  className = '',
  disabled,
  ...props
}: ButtonProps) {
  const { theme } = useTheme()

  // Size styles
  const sizeStyles = {
    sm: 'px-4 py-2 text-xs',
    md: 'px-8 py-3 text-sm',
    lg: 'px-12 py-4 text-sm',
  }

  // Base styles (common to all variants)
  const baseStyles = 'font-bold uppercase tracking-wider rounded-lg transition-all duration-300 inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed'

  // Get variant-specific styles
  const getVariantStyles = () => {
    switch (variant) {
      case 'primary':
        return {
          backgroundColor: theme.colors.primary,
          color: '#ffffff',
          hover: 'hover:shadow-xl hover:scale-105',
        }
      case 'secondary':
        return {
          backgroundColor: theme.colors.secondary || theme.colors.primaryLight || '#666666',
          color: '#ffffff',
          hover: 'hover:shadow-lg hover:scale-105',
        }
      case 'outline':
        return {
          backgroundColor: 'transparent',
          color: theme.colors.primary,
          border: `2px solid ${theme.colors.primary}`,
          hover: 'hover:bg-opacity-10',
          hoverBg: theme.colors.primary,
        }
      case 'ghost':
        return {
          backgroundColor: 'transparent',
          color: theme.colors.text,
          hover: 'hover:bg-opacity-10',
          hoverBg: theme.colors.surface,
        }
      default:
        return {
          backgroundColor: theme.colors.primary,
          color: '#ffffff',
          hover: 'hover:shadow-xl hover:scale-105',
        }
    }
  }

  const variantStyles = getVariantStyles()
  const isDisabled = disabled || loading

  return (
    <button
      className={`${baseStyles} ${sizeStyles[size]} ${variantStyles.hover} ${fullWidth ? 'w-full' : ''} ${className}`}
      style={{
        backgroundColor: variantStyles.backgroundColor,
        color: variantStyles.color,
        border: variantStyles.border,
      }}
      disabled={isDisabled}
      onMouseEnter={(e) => {
        if (!isDisabled && variantStyles.hoverBg) {
          e.currentTarget.style.backgroundColor = `${variantStyles.hoverBg}20`
        }
      }}
      onMouseLeave={(e) => {
        if (!isDisabled) {
          e.currentTarget.style.backgroundColor = variantStyles.backgroundColor || 'transparent'
        }
      }}
      {...props}
    >
      {loading ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Loading...</span>
        </>
      ) : (
        <>
          {icon && <span className="w-4 h-4">{icon}</span>}
          <span>{children}</span>
        </>
      )}
    </button>
  )
}
